/**
 * Feedback Modal Component
 * Slide-in modal from right side for submitting feedback
 */

import DOMPurify from 'dompurify';
import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Loader2 } from 'lucide-react';
import { useFeedback } from '../../contexts/FeedbackContext';
import { submitFeedback, getRemainingSubmissions, getBrowserInfo } from '../../services/feedbackService';
import { createNotification } from '../../services/notificationService';
import { useSubscriptions } from '../../contexts/SubscriptionContext';
import { useNotifications } from '../../contexts/NotificationContext';

const FeedbackModal = () => {
  const { showModal, selectedElement, closeModal } = useFeedback();
  const { user } = useSubscriptions();
  const { fetchNotifications } = useNotifications();

  // Form state
  const [issueType, setIssueType] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [remainingSubmissions, setRemainingSubmissions] = useState(5);
  const [elementSnapshot, setElementSnapshot] = useState(null);

  // Load remaining submissions and capture element snapshot when modal opens
  useEffect(() => {
    if (showModal) {
      loadRemainingSubmissions();
      captureElementSnapshot();
    }
  }, [showModal]);

  const captureElementSnapshot = () => {
    try {
      if (selectedElement?.element) {
        // Capture the outerHTML of the selected element
        const html = selectedElement.element.outerHTML;
        setElementSnapshot(html);
        console.log('📸 Captured element snapshot:', html.substring(0, 100) + '...');
      }
    } catch (err) {
      console.error('Error capturing element snapshot:', err);
      setElementSnapshot(null);
    }
  };

  const loadRemainingSubmissions = async () => {
    try {
      const remaining = await getRemainingSubmissions(null);
      setRemainingSubmissions(remaining);
    } catch (err) {
      console.error('Error loading remaining submissions:', err);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!title.trim()) {
      setError('Please provide a title');
      return;
    }

    if (title.length > 100) {
      setError('Title must be 100 characters or less');
      return;
    }

    if (!description.trim()) {
      setError('Please provide a description');
      return;
    }

    if (description.length > 500) {
      setError('Description must be 500 characters or less');
      return;
    }

    if (expectedBehavior.length > 300) {
      setError('Expected behavior must be 300 characters or less');
      return;
    }

    // Prepare feedback data
    const feedbackData = {
      issueType,
      title: title.trim(),
      description: description.trim(),
      expectedBehavior: expectedBehavior.trim() || null,
      pageUrl: window.location.href,
      elementPath: selectedElement?.path || 'unknown',
      elementSnapshot: elementSnapshot,
      browserInfo: getBrowserInfo()
    };

    // Store for notification
    const submittedTitle = title.trim();

    // Reset form immediately
    setTitle('');
    setDescription('');
    setExpectedBehavior('');
    setIssueType('bug');
    setError(null);

    // Close modal immediately
    closeModal();

    // Submit in background (non-blocking)
    // Using Promise without await so it doesn't block the UI
    submitFeedback(feedbackData)
      .then((result) => {
        console.log('✅ Feedback submitted successfully:', result);

        // Show platform notification
        if (user?.id) {
          createNotification(
            user.id,
            'feedback_submitted',
            'Thanks for your feedback!',
            `We've received your feedback${submittedTitle ? `: "${submittedTitle.substring(0, 50)}${submittedTitle.length > 50 ? '...' : ''}"` : ''}. We'll review it soon.`,
            { feedbackId: result.feedback?.id }
          ).then(() => {
            // Manually refresh notifications to show immediately (don't wait for Realtime)
            fetchNotifications();
          }).catch(notifErr => {
            console.error('Error creating notification:', notifErr);
          });
        }

        setRemainingSubmissions(result.remaining);
      })
      .catch((err) => {
        console.error('❌ Error submitting feedback:', err);

        // Show error notification if submission failed
        if (user?.id) {
          createNotification(
            user.id,
            'feedback_error',
            'Feedback Submission Failed',
            err.message || 'Failed to submit feedback. Please try again.',
            {}
          ).catch(notifErr => {
            console.error('Error creating error notification:', notifErr);
          });
        }
      });
  };

  const handleClose = () => {
    if (!loading) {
      closeModal();
      // Reset form after animation
      setTimeout(() => {
        setTitle('');
        setDescription('');
        setExpectedBehavior('');
        setIssueType('bug');
        setError(null);
      }, 300);
    }
  };

  if (!showModal) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[9999] animate-fade-in"
        onClick={handleClose}
      />

      {/* Modal - slides in from right */}
      <div className={`feedback-modal fixed right-0 top-0 h-full w-full sm:w-[500px] bg-white shadow-2xl z-[10000] overflow-y-auto ${showModal ? 'animate-slide-in' : 'animate-slide-out'}`}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Submit Feedback</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {remainingSubmissions}/5 submissions remaining today
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Error</p>
                <p className="text-xs text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Issue Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Issue Type *
              </label>
              <select
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
              >
                <option value="bug">Bug - Something isn't working</option>
                <option value="feature_request">Feature Request - New functionality</option>
                <option value="ui_issue">UI Issue - Design or layout problem</option>
                <option value="other">Other - General feedback</option>
              </select>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title * <span className="text-xs text-gray-500">({title.length}/100)</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={loading}
                placeholder="Brief summary of the issue..."
                maxLength={100}
                required
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description * <span className="text-xs text-gray-500">({description.length}/500)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={loading}
                placeholder="Please describe the issue or feedback in detail..."
                rows={5}
                maxLength={500}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 resize-none"
              />
            </div>

            {/* Expected Behavior */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Behavior <span className="text-xs text-gray-500">(optional, {expectedBehavior.length}/300)</span>
              </label>
              <textarea
                value={expectedBehavior}
                onChange={(e) => setExpectedBehavior(e.target.value)}
                disabled={loading}
                placeholder="What did you expect to happen?"
                rows={3}
                maxLength={300}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 resize-none"
              />
            </div>

            {/* Element Snapshot Preview */}
            {elementSnapshot && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Element Preview
                </label>
                <div className="w-full p-4 bg-gray-50 border border-gray-300 rounded-lg overflow-hidden">
                  <div
                    className="element-snapshot-preview max-h-[200px] overflow-auto border border-gray-200 bg-white rounded"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(elementSnapshot) }}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Preview of the element you're providing feedback on
                  </p>
                </div>
              </div>
            )}

            {/* Element Path (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Element Path
              </label>
              <input
                type="text"
                value={selectedElement?.path || 'unknown'}
                disabled
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-600"
              />
            </div>

            {/* Page URL (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Page URL
              </label>
              <input
                type="text"
                value={window.location.href}
                disabled
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-600 truncate"
              />
            </div>

            {/* Submit buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !description.trim() || remainingSubmissions === 0}
                className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Feedback'
                )}
              </button>
            </div>
          </form>

          {/* Rate limit warning */}
          {remainingSubmissions === 0 && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-800">
                You've reached your daily limit of 5 feedback submissions. Limit resets at midnight.
              </p>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slide-in {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        @keyframes slide-out {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(100%);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-in-out;
        }

        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }

        .animate-slide-out {
          animation: slide-out 0.3s ease-in;
        }
      `}</style>
    </>
  );
};

export default FeedbackModal;
